'use client';

/**
 * Cookie Consent Banner
 * Displays at the bottom of the page until user accepts or declines cookies
 * Stores preference in localStorage and dispatches events for analytics integration
 */

import { useState, useEffect } from 'react';
import LocaleLink from './LocaleLink';
import { cookieBannerTranslations, CookieBannerLocale } from '../translations/cookieBanner';

const CONSENT_KEY = 'finley-cookie-consent';

export type CookieConsentValue = 'accepted' | 'declined' | null;

/**
 * Get current cookie consent status from localStorage
 */
export const getCookieConsent = (): CookieConsentValue => {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(CONSENT_KEY);
  return stored as CookieConsentValue;
};

/**
 * Set cookie consent and dispatch event for analytics
 */
export const setCookieConsent = (value: 'accepted' | 'declined') => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, value);

  // Trigger custom event so analytics can react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: value }));
};

interface CookieBannerProps {
  locale?: string;
}

export default function CookieBanner({ locale = 'en' }: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const t = cookieBannerTranslations[locale as CookieBannerLocale] || cookieBannerTranslations.en;

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (consent === null) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookieConsent('accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    setCookieConsent('declined');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed left-4 right-4 z-50 animate-fade-in"
      style={{
        bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        maxWidth: '56rem',
        margin: '0 auto',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        className="rounded-3xl shadow-2xl p-6 sm:p-8"
        style={{
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.95), rgba(249, 247, 244, 0.95))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(136, 199, 219, 0.2)',
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          {/* Content */}
          <div className="flex-1">
            <h3 className="text-h4 text-night mb-2 font-display font-regular">
              {t.title}
            </h3>
            <p className="text-body font-normal text-night/70 leading-relaxed">
              {t.description}{' '}
              <LocaleLink
                href="/cookie-policy"
                locale={locale}
                className="text-sky-blue hover:underline transition-colors"
              >
                {t.learnMore}
              </LocaleLink>
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={handleAccept}
              className="px-6 py-3 rounded-full text-body font-medium transition-all duration-200 bg-tea text-night hover:bg-stronger-tea shadow-lg order-1 sm:order-2 whitespace-nowrap"
            >
              {t.acceptAll}
            </button>
            <button
              onClick={handleDecline}
              className="px-6 py-3 rounded-full text-body font-medium transition-all duration-200 bg-baby-powder border border-night/20 text-night hover:bg-baby-powder/80 hover:border-night/40 order-2 sm:order-1 whitespace-nowrap"
            >
              {t.essentialOnly}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
